import { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NewsletterPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const hasSeenPopup = localStorage.getItem('nexa_newsletter_popup');
    // Don't show popup on checkout or cart pages
    const isCheckoutOrCart = window.location.pathname.includes('checkout') || window.location.pathname.includes('cart');
    if (!hasSeenPopup && !isCheckoutOrCart) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 15000); // Show after 15 seconds
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('nexa_newsletter_popup', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Welcome! Check your email for 10% off.');
      handleClose();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 popup-overlay"
          onClick={handleClose}
          data-testid="newsletter-popup"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1616046229478-9901c5536a45?w=800"
                alt="Beautiful home decor"
                className="w-full h-48 object-cover"
              />
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5 hover:bg-white transition-colors"
                data-testid="close-newsletter-popup"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-8 text-center">
              <h3 className="font-serif text-3xl font-semibold text-nexa-charcoal mb-2">
                Get 10% Off
              </h3>
              <p className="text-muted-foreground mb-6">
                Join our mailing list for exclusive offers and decor inspiration.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  required
                  data-testid="newsletter-email-input"
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 btn-gold"
                  disabled={loading}
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? 'Subscribing...' : 'Get My 10% Off'}
                </Button>
              </form>
              
              <p className="text-xs text-muted-foreground mt-4">
                By subscribing, you agree to our Privacy Policy.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
